<script>
  import Mofibo from '../svg/Mofibo.svelte';
  import Storytel from '../svg/Storytel.svelte';

  export let isLoggedIn = false,
    color = 'pink',
    data = null,
    mofibo = false,
    homepage = null,
    testId = 'topNavigation',
    useTestIds = undefined;

  let navElement;
  $: logoLink = homepage ? homepage : data ? `/${data.countryIso}/${data.language}/` : '';
  $: isScrolled = false;
  $: hasOpenDropdown = false;
  $: isMenuOpened = false;
  $: menuItems = (data && data.items) || [];

  const onScroll = () => (isScrolled = window.pageYOffset > 10);

  const closeOtherDropdowns = (self) => {
    hasOpenDropdown = false;
    const dropdowns = navElement.querySelectorAll('.item-list input[type="checkbox"]');
    dropdowns.forEach((dropdown) => {
      if (dropdown !== self) {
        dropdown.checked = false;
      }
    });
  };
  const onDropdown = (event) => {
    if (event.target.checked) {
      closeOtherDropdowns(event.target);
      hasOpenDropdown = true;
    } else {
      hasOpenDropdown = false;
    }
  };
  const onMenuToggle = (event) => {
    isMenuOpened = event.target.checked;
    closeOtherDropdowns();
  };
</script>

<style lang="scss">
  /* stylelint-disable no-descending-specificity */

  /* Navigation CSS is written in a mobile first approach */
  @import '../../styles/settings/colors';

  a {
    color: inherit;
  }

  .for-logged-in {
    :not(.is-logged-in) & {
      display: none;
    }

    .is-logged-in & {
      display: inherit;
    }
  }

  .for-logged-out {
    :not(.is-logged-in) & {
      display: inherit;
    }

    .is-logged-in & {
      display: none;
    }
  }

  // Accessible menu
  .vh {
    // visually hidden
    position: absolute !important;
    clip: rect(1px, 1px, 1px, 1px);
    padding: 0 !important;
    border: 0 !important;
    height: 1px !important;
    width: 1px !important;
    overflow: hidden;
  }

  :checked + label {
    .expanded-text {
      display: inline;
    }

    .collapsed-text {
      display: none;
    }
  }

  [type='checkbox']:not(:checked) + label {
    .expanded-text {
      display: none;
    }

    .collapsed-text {
      display: inline;
    }
  }

  [type='checkbox']:focus-visible + label,
  .item-link:focus-visible {
    outline: auto;
    outline-color: $orange-50;
    outline-offset: 2px;
    outline-offset: 2px;
    padding: 4px;

    &:not(.mobile-menu-button) {
      margin: -4px;
    }
  }

  .logo-link:focus-visible {
    outline-color: $orange-50;
  }

  // General layout and colors
  .main-nav {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    width: 100%;
    height: 8rem;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 0;
    padding: 0 2.4rem;
    transition: background-color 0.3s linear, color 0.3s linear;

    &::after {
      display: block;
      pointer-events: none;
      content: '';
      position: fixed;
      top: 8rem;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: transparent;
      z-index: -1;
      transition: background-color 0.3s linear;
    }

    &.has-open-menu::after {
      background-color: rgba($black, 0.5);
      pointer-events: all;
    }

    &.pink {
      background-color: $pink-50;
      color: $purple-50;
      fill: $purple-50;

      .mobile-menu-button > span {
        background-color: $purple-50;
      }
    }

    &.purple {
      background-color: $purple-50;
      color: $pink-50;
      fill: $pink-50;

      .mobile-menu-button > span {
        background-color: $pink-50;
      }
    }

    &.white,
    &.is-scrolled,
    &.is-expanded,
    &.has-open-menu {
      background-color: $white;
      color: $black;
      fill: $black;

      .mobile-menu-button > span {
        background-color: $black;
      }
    }

    &.transparent {
      .mobile-menu-button > span {
        background-color: $black;
      }
    }
  }

  .logo {
    display: flex;
    height: 100%;
    width: 12.6rem;
    margin: 0;

    &.portrait {
      width: 7.6rem;
      display: none;
    }

    &.mofibo {
      width: 10.2rem;
    }
  }

  // Navigation items (top level)
  .item-list {
    position: fixed;
    top: 8rem;
    right: 0; // left: 0 for rtl
    width: 100%;
    height: calc(100vh - 8rem);
    margin: 0;
    padding: 1.6rem;
    overflow: auto;
    flex-direction: column;
    align-content: center;
    background-color: $white;
    color: $black;
    font-size: 1.6rem;
    transform: translateX(100%);
    transition: transform 0.3s linear;
    display: flex; // flex if checked and on desktop

    [type='checkbox']:checked ~ & {
      transform: translateX(0);
    }

    :global([dir='rtl']) & {
      right: auto;
      left: 0;
      @media only screen and (max-width: 1199px) {
        transform: translateX(-100%);
      }
    }
  }

  .item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    transition: visibility 0.3s linear;

    &:not(:first-child) {
      margin-block-start: 2.4rem;
    }
  }

  .item-link {
    font-weight: inherit;
    text-decoration: none;
  }

  .item-link:hover,
  .item-label:hover {
    cursor: pointer;
    color: $orange-50;
    border-color: $orange-50;
  }

  // Naviation items with sub items (checkboxes)
  [type='checkbox']:checked {
    ~ .item-label {
      color: $orange-50;

      .item-arrow {
        border-color: $orange-50;
      }
    }
  }

  [type='checkbox']:not(:checked) ~ [role='menu'] {
    /* Fade in effect for desktop */
    @media only screen and (max-width: 1199px) {
      &.sub-item-list {
        display: none;
      }

      .item {
        visibility: hidden;
      }
    }

    &.sub-item-list .item {
      display: none;
    }
  }

  .item-label {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .item-arrow {
    position: relative;
    top: 4px;
    width: 1rem;
    height: 1rem;
    margin-inline-start: 1.2rem;
    transform-origin: center;
    transition: transform 0.3s;
    transform: translateY(-5px) rotate(135deg);

    &::before {
      content: '';
      display: block;
      width: 100%;
      height: 100%;
      border-width: 2px 2px 0 0;
      border-style: solid;
      border-color: inherit;
    }

    :checked ~ .item-label & {
      transform: translateY(0) rotate(-45deg);
    }
  }

  // Navigation sub items
  .sub-item-list {
    display: block;
    padding: 1.6rem;
    overflow: hidden;
    max-height: 200vh; // large enough to never end
    opacity: 1;

    &.grid {
      display: grid;
      grid-template-columns: repeat(2, 22rem);
      padding-block-start: 0;

      .item {
        margin-block-start: 2.4rem;
      }
    }
  }

  // Hamburger menu (mobile only) inspired by https://codepen.io/designcouch/pen/Atyop
  .mobile-menu-button {
    width: 4.4rem;
    height: 4rem;
    position: relative;
    cursor: pointer;
    align-self: center;
    border: 1rem solid transparent; // give button more space

    // Button design and effects
    > span {
      position: absolute;
      height: 0.3rem;
      border-radius: 0.9rem;
      width: 100%;
      opacity: 1;
      left: 0;
      top: 0;
      transform: rotate(0deg);
      transition: all 0.25s ease-in-out;

      &:first-child {
        top: 0;
      }

      &:nth-child(2),
      &:nth-child(3) {
        top: 0.9rem;
      }

      &:nth-child(4) {
        top: 1.8rem;
      }

      [type='checkbox']:checked ~ & {
        &:first-child,
        &:nth-child(4) {
          top: 0.9rem;
          width: 0%;
          left: 50%;
        }

        &:nth-child(2) {
          transform: rotate(45deg);
        }

        &:nth-child(3) {
          transform: rotate(-45deg);
        }
      }
    }
  }

  @media only screen and (min-width: 600px) {
    .item-list {
      width: 54rem;
    }

    .main-nav {
      padding: 0 3.2rem;
    }
  }

  @media only screen and (min-width: 900px) {
    .main-nav {
      padding: 0 8rem;
    }
  }

  @media only screen and (min-width: 1200px) {
    .main-nav {
      padding: 0 calc((100vw - 112rem) / 2);
    }

    .logo {
      &.landscape {
        display: none;
      }

      &.portrait {
        display: flex;
      }
    }

    .item-list {
      display: flex !important;
      position: initial;
      width: auto;
      height: auto;
      padding: 0;
      flex-direction: row;
      overflow: visible;
      background-color: transparent;
      color: inherit;
      transform: translateX(0);
    }

    .item {
      margin-block-start: 0 !important;

      &:not(:first-child) {
        margin-inline-start: 4rem;
      }
    }

    .sub-item-list {
      display: block;
      position: absolute;
      top: 8rem;
      min-width: 24rem;
      padding: 0;
      background-color: $white;
      color: $black;
      box-shadow: 0 0.3rem 0.1rem rgba($black, 0.16), 0 0.4rem 0.6rem rgba($black, 0.23);
      transition: max-height 0.6s, opacity 0.2s ease-in;

      // for loggged in and logged out state
      .item:last-child &,
      .item:nth-last-child(2) & {
        right: 0;

        :global([dir='rtl']) & {
          left: 0;
          right: auto;
        }
      }

      [type='checkbox']:not(:checked) ~ & {
        max-height: 0;
        opacity: 0;
      }

      &.grid {
        right: 5rem;
        grid-template-columns: repeat(4, 22rem);

        :global([dir='rtl']) & {
          right: auto;
          left: 5rem;
        }
      }

      .item {
        margin: 0;
        padding: 1.6rem;
      }
    }

    .mobile-menu-button,
    .mobile-menu-checkbox {
      display: none !important;
    }
  }

  @media only screen and (min-width: 1800px) {
    .main-nav {
      padding: 0 calc((100vw - 140.8rem) / 2);
    }
  }

  /* stylelint-enable no-descending-specificity */
</style>

<svelte:window on:scroll={onScroll} />
<nav
  class={`main-nav ${color}`}
  aria-label="Page navigation"
  data-testid={useTestIds && testId}
  class:is-logged-in={isLoggedIn}
  class:is-scrolled={isScrolled}
  class:has-open-menu={isMenuOpened}
  class:is-expanded={hasOpenDropdown}
  bind:this={navElement}
>
  <a href={logoLink} aria-label="Home" rel="external" className="logo-link">
    {#if mofibo}
      <figure class="logo mofibo">
        <Mofibo />
      </figure>
    {:else}
      <figure class="logo landscape">
        <Storytel />
      </figure>
      <figure class="logo portrait">
        <Storytel format="portrait" />
      </figure>
    {/if}
  </a>
  <input
    type="checkbox"
    role="button"
    aria-haspopup="true"
    autocomplete="off"
    id="mobile-menu"
    class="vh mobile-menu-checkbox"
    on:change={onMenuToggle}
  />
  <label for="mobile-menu" class="mobile-menu-button">
    <span />
    <span />
    <span />
    <span />
    <span class="vh">Menu</span>
    <span class="vh expanded-text">expanded</span>
    <span class="vh collapsed-text">collapsed</span>
  </label>
  <ul role="menu" class="item-list">
    {#each menuItems as item}
      <li
        role={`${item.url ? 'menuitem' : 'menuitemcheckbox'}`}
        class="item"
        class:for-logged-in={item.showForLoggedIn === true}
        class:for-logged-out={item.showForLoggedOut === true}
        data-testid={useTestIds && item.name.replace(' ', '-')}
      >
        {#if item.url}
          <a href={item.url} rel="external" class="item-link">{item.name}</a>
        {:else}
          <input
            type="checkbox"
            role="button"
            aria-haspopup="true"
            autocomplete="off"
            id={`${item.name.replace(' ', '-')}-nav-item`}
            class="vh"
            on:change={onDropdown}
          />
          <label class="item-label" for={`${item.name.replace(' ', '-')}-nav-item`}>
            {item.name}
            <span class="vh expanded-text">expanded</span>
            <span class="vh collapsed-text">collapsed</span>
            <div class="item-arrow" />
          </label>
          <ul role="menu" class="sub-item-list" class:grid={item.items.length > 5}>
            {#each item.items as subItem}
              <li class="item" data-testid={useTestIds && subItem.name} role="menuitem">
                <a href={subItem.url} rel="external" class="item-link">
                  {#if subItem.iconUrl}
                    <img src={subItem.iconUrl} class="item-icon" alt="" aria-hidden="true" />
                  {/if}
                  {subItem.name}
                </a>
              </li>
            {/each}
          </ul>
        {/if}
      </li>
    {/each}
  </ul>
</nav>
